import React, { useCallback, useRef, useState } from "react";
import {
    GoogleMap,
    Marker,
    StandaloneSearchBox,
    useLoadScript,
} from "@react-google-maps/api";
import "./PropertyMapPicker.css";

const GOOGLE_MAPS_LIBRARIES = ["places"];

// Default center: Kathmandu, Nepal
const NEPAL_CENTER = { lat: 27.7172, lng: 85.324 };

/**
 * PropertyMapPicker
 *
 * Props:
 *   lat       {number|null}  – current latitude value
 *   lng       {number|null}  – current longitude value
 *   onChange  {function}     – called with normalized location payload on pin change
 *   onLocationSelect {function} – backward-compatible alias of onChange
 *   isNe      {boolean}      – render labels in Nepali when true
 *   propertyType {string}    – property type to symbolize marker icon
 */
function PropertyMapPicker({ lat, lng, onChange, onLocationSelect, isNe = false, propertyType = "" }) {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

    const { isLoaded, loadError } = useLoadScript({
        googleMapsApiKey: apiKey || "",
        libraries: GOOGLE_MAPS_LIBRARIES,
    });

    const searchBoxRef = useRef(null);
    const mapRef = useRef(null);
    const BRAND_ORANGE = "#f06c24";
    const BRAND_NAVY = "#0f2747";

    const resolveLocationParts = (result) => {
        const components = Array.isArray(result?.address_components)
            ? result.address_components
            : [];

        const getByType = (type) => {
            const comp = components.find((item) => item.types?.includes(type));
            return comp ? comp.long_name : "";
        };

        return {
            state: getByType("administrative_area_level_1"),
            district: getByType("administrative_area_level_2"),
            municipality:
                getByType("locality") ||
                getByType("administrative_area_level_3") ||
                getByType("sublocality"),
        };
    };

    const emitLocation = useCallback(
        ({ lat: nextLat, lng: nextLng, address = "", result = null }) => {
            const payload = {
                lat: nextLat,
                lng: nextLng,
                coordinates: { lat: nextLat, lng: nextLng },
                address,
                ...resolveLocationParts(result),
            };

            if (typeof onChange === "function") {
                onChange(payload);
            }
            if (typeof onLocationSelect === "function") {
                onLocationSelect(payload);
            }
        },
        [onChange, onLocationSelect]
    );

    // marker position derived from props; fall back to Nepal centre
    const markerPos = Number.isFinite(Number(lat)) && Number.isFinite(Number(lng))
        ? { lat: Number(lat), lng: Number(lng) }
        : null;

    const [mapCenter, setMapCenter] = useState(markerPos || NEPAL_CENTER);

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
    }, []);

    const getTypeMarkerMeta = useCallback((type) => {
        const normalized = String(type || "").toLowerCase().trim();
        switch (normalized) {
            case "house":
                return { iconKey: "house" };
            case "apartment":
                return { iconKey: "apartment" };
            case "villa":
                return { iconKey: "villa" };
            case "studio":
                return { iconKey: "studio" };
            case "cottage":
                return { iconKey: "cottage" };
            case "land":
                return { iconKey: "land" };
            case "commercial":
                return { iconKey: "commercial" };
            case "condo":
                return { iconKey: "condo" };
            case "cabin":
                return { iconKey: "cabin" };
            case "room":
                return { iconKey: "room" };
            default:
                return { iconKey: "default" };
        }
    }, []);

    const getIconMarkup = useCallback((iconKey) => {
        switch (iconKey) {
            case "house":
                return '<path d="M12 23v-5.8L20 11l8 6.2V23h-5.5v-4.2h-5V23z" fill="#0f2747"/>';
            case "apartment":
                return '<rect x="13" y="12" width="14" height="12" rx="1.2" fill="#0f2747"/><rect x="16" y="15" width="2" height="2" fill="#fff"/><rect x="20" y="15" width="2" height="2" fill="#fff"/><rect x="24" y="15" width="2" height="2" fill="#fff"/><rect x="16" y="19" width="2" height="2" fill="#fff"/><rect x="20" y="19" width="2" height="2" fill="#fff"/>';
            case "villa":
                return '<path d="M11 22v-4.8L20 10l9 7.2V22h-4.8v-3.8h-8.4V22z" fill="#0f2747"/><circle cx="10.5" cy="20.5" r="2.5" fill="#0f2747"/>';
            case "studio":
                return '<rect x="12" y="14" width="16" height="10" rx="2" fill="#0f2747"/><circle cx="20" cy="19" r="3" fill="#fff"/><circle cx="20" cy="19" r="1.6" fill="#0f2747"/><rect x="14" y="12" width="4" height="2" fill="#0f2747"/>';
            case "cottage":
                return '<path d="M12.5 22v-4.5L20 12l7.5 5.5V22h-3.8v-3h-7.4v3z" fill="#0f2747"/><path d="M14.5 14.2h11" stroke="#0f2747" stroke-width="1.4"/>';
            case "land":
                return '<path d="M20 24c-4.4-2-5.8-5.2-4.8-9.2 4.1.5 6.6 2.9 7 6.8" fill="none" stroke="#0f2747" stroke-width="1.7" stroke-linecap="round"/><path d="M20 24c4.4-2 5.8-5.2 4.8-9.2-4.1.5-6.6 2.9-7 6.8" fill="none" stroke="#0f2747" stroke-width="1.7" stroke-linecap="round"/>';
            case "commercial":
                return '<rect x="13" y="15" width="14" height="9" rx="1.8" fill="#0f2747"/><rect x="17" y="13" width="6" height="2" rx="1" fill="#0f2747"/><rect x="19" y="18" width="2" height="2" fill="#fff"/>';
            case "condo":
                return '<rect x="14" y="11" width="12" height="13" rx="1.2" fill="#0f2747"/><rect x="16.5" y="14" width="2" height="2" fill="#fff"/><rect x="21.5" y="14" width="2" height="2" fill="#fff"/><rect x="16.5" y="18" width="2" height="2" fill="#fff"/><rect x="21.5" y="18" width="2" height="2" fill="#fff"/>';
            case "cabin":
                return '<path d="M13 22v-4.8L20 12l7 5.2V22h-3.5v-3.2h-7V22z" fill="#0f2747"/><path d="M12.8 17.7h14.4M12.8 19.6h14.4" stroke="#fff" stroke-width="1.2"/>';
            case "room":
                return '<rect x="12.5" y="18" width="15" height="4" rx="1.2" fill="#0f2747"/><rect x="13.5" y="16" width="5.2" height="2" rx="0.7" fill="#0f2747"/><rect x="12.5" y="22" width="1.6" height="2" fill="#0f2747"/><rect x="25.9" y="22" width="1.6" height="2" fill="#0f2747"/>';
            default:
                return '<circle cx="20" cy="19" r="3.2" fill="#0f2747"/>';
        }
    }, []);

    const markerIcon = useCallback(() => {
        if (!window.google) return null;
                const { iconKey } = getTypeMarkerMeta(propertyType);
                const size = 40;
                const iconMarkup = getIconMarkup(iconKey).replace(/#0f2747/g, BRAND_NAVY);
        const svg = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 40 46">
                        <path d="M20 2C11.2 2 4 9.2 4 18c0 11.3 14.3 23.7 15 24.2a1.6 1.6 0 0 0 2 0C21.7 41.7 36 29.3 36 18 36 9.2 28.8 2 20 2z" fill="${BRAND_ORANGE}" stroke="${BRAND_NAVY}" stroke-width="2"/>
                        <circle cx="20" cy="18" r="9.2" fill="#ffffff"/>
                        ${iconMarkup}
          </svg>
        `;
        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
            scaledSize: new window.google.maps.Size(size, size),
                        anchor: new window.google.maps.Point(size / 2, size - 2),
        };
        }, [BRAND_NAVY, BRAND_ORANGE, getIconMarkup, getTypeMarkerMeta, propertyType]);

    // User clicks on the map → place/move pin, reverse-geocode address
    const handleMapClick = useCallback(
        (e) => {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();

            if (!window.google) return;

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
                const primary = status === "OK" && results[0] ? results[0] : null;
                const address = primary ? primary.formatted_address : "";
                setMapCenter({ lat: newLat, lng: newLng });
                emitLocation({ lat: newLat, lng: newLng, address, result: primary });
            });
        },
        [emitLocation]
    );

    // User drags the marker
    const handleMarkerDrag = useCallback(
        (e) => {
            const newLat = e.latLng.lat();
            const newLng = e.latLng.lng();

            if (!window.google) return;

            const geocoder = new window.google.maps.Geocoder();
            geocoder.geocode({ location: { lat: newLat, lng: newLng } }, (results, status) => {
                const primary = status === "OK" && results[0] ? results[0] : null;
                const address = primary ? primary.formatted_address : "";
                setMapCenter({ lat: newLat, lng: newLng });
                emitLocation({ lat: newLat, lng: newLng, address, result: primary });
            });
        },
        [emitLocation]
    );

    // User picks a place from autocomplete search
    const handlePlacesChanged = useCallback(() => {
        if (!searchBoxRef.current) return;
        const places = searchBoxRef.current.getPlaces();
        if (!places || places.length === 0) return;

        const place = places[0];
        if (!place.geometry || !place.geometry.location) return;

        const newLat = place.geometry.location.lat();
        const newLng = place.geometry.location.lng();
        const address = place.formatted_address || place.name || "";

        setMapCenter({ lat: newLat, lng: newLng });
        if (mapRef.current) {
            mapRef.current.panTo({ lat: newLat, lng: newLng });
            mapRef.current.setZoom(16);
        }
        emitLocation({ lat: newLat, lng: newLng, address, result: place });
    }, [emitLocation]);

    if (!apiKey) {
        return (
            <div className="map-picker-api-missing">
                <span>&#9888;</span>
                <p>
                    Google Maps API key not configured.<br />
                    Add <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> to your <code>.env</code> file.
                    <br />
                    <a
                        href="https://developers.google.com/maps/documentation/javascript/get-api-key"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Get an API key →
                    </a>
                </p>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="map-picker-api-missing">
                <span>&#9888;</span>
                <p>Failed to load Google Maps. Check your API key and network connection.</p>
            </div>
        );
    }

    if (!isLoaded) {
        return (
            <div className="map-picker-loading">
                <div className="spinner-border spinner-border-sm text-secondary" role="status" />
                <span>{isNe ? "नक्शा लोड हुँदैछ…" : "Loading map…"}</span>
            </div>
        );
    }

    return (
        <div>
            <div className="map-picker-wrapper">
                {/* Floating search box */}
                <div className="map-picker-search-bar">
                    <StandaloneSearchBox
                        onLoad={(ref) => { searchBoxRef.current = ref; }}
                        onPlacesChanged={handlePlacesChanged}
                    >
                        <input
                            type="text"
                            placeholder={
                                isNe
                                    ? "स्थान खोज्नुहोस् (जस्तै: ठमेल, काठमाडौं)"
                                    : "Search location (e.g. Thamel, Kathmandu)"
                            }
                            aria-label="Search location"
                        />
                    </StandaloneSearchBox>
                </div>

                <GoogleMap
                    mapContainerClassName="map-picker-canvas"
                    center={mapCenter}
                    zoom={markerPos ? 16 : 12}
                    onClick={handleMapClick}
                    onLoad={onMapLoad}
                    options={{
                        streetViewControl: false,
                        mapTypeControl: false,
                        fullscreenControl: true,
                        zoomControl: true,
                    }}
                >
                    {markerPos && (
                        <Marker
                            position={markerPos}
                            draggable
                            onDragEnd={handleMarkerDrag}
                            icon={markerIcon() || undefined}
                            title={isNe ? "सम्पत्तिको स्थान" : "Property location"}
                        />
                    )}
                </GoogleMap>
            </div>

            <p className="map-picker-hint mt-2">
                {isNe
                    ? "नक्शामा क्लिक गर्नुहोस् वा मार्कर तान्नुहोस् सम्पत्तिको सटिक स्थान राख्न।"
                    : "Click on the map or drag the marker to pin your property's exact location."}
            </p>

            {markerPos && (
                <p className="map-picker-coords">
                    &#128204;{" "}
                    {isNe ? "अक्षांश" : "Lat"}: {markerPos.lat.toFixed(6)},{" "}
                    {isNe ? "देशान्तर" : "Lng"}: {markerPos.lng.toFixed(6)}
                </p>
            )}
        </div>
    );
}

export default PropertyMapPicker;
